<template>
  <div>
    <Draggable
      v-model="images"
      :class="$style.grid"
      item-key="id"
      chosen-class="gallery-chosen"
      drag-class="gallery-drag"
    >
      <template #item="{ element }">
        <Image
          :imageUrl="element.url"
          @remove="removeImage(element.id)"
        />
      </template>

      <template #footer>
        <label
          v-if="canAddImage"
          :class="[
            $style.dropzone,
            {
              [$style.dropzoneEmpty]: !hasImages,
              [$style.dropzoneDragging]: isDraggingOver,
              [$style.dropzoneUploading]: isUploading,
            },
          ]"
          @dragenter.prevent="isDraggingOver = true"
          @dragover.prevent="isDraggingOver = true"
          @dragleave.prevent="isDraggingOver = false"
          @drop.prevent="handleDrop"
        >
          <input
            :class="$style.input"
            type="file"
            accept="image/*"
            multiple
            :disabled="isUploading"
            @change="handleFileChange"
            v-test="'_base-gallery-input'"
          >
          <div :class="$style.icon">
            <BaseSpinner
              v-if="isUploading"
              inline
            />
            <BaseIcon
              v-else
              name="image"
              size="l"
              color="primary"
            />
          </div>
          <BaseText center>{{ uploadStatusText }}</BaseText>
          <BaseText
            v-if="!hasImages"
            color="secondary"
            size="s"
          >
            {{ t('global.gallery.file_requirements') }}
          </BaseText>
          <BaseText
            v-if="showFileSizeError"
            color="error"
            size="s"
          >
            {{ t('global.file_too_large', { size: '10mb' }) }}
          </BaseText>
          <BaseText
            v-else-if="uploadFailure"
            color="error"
            size="s"
          >
            {{ uploadFailureText }}
          </BaseText>
        </label>
      </template>
    </Draggable>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useCloudinaryUpload } from './cloudinary-upload';
import {
  findImageRejection,
  hasImageRequirements,
  type ImageRejection,
} from './helpers/image-dimensions';
import {
  type ImaginaryCategory,
  isHeicFile,
  s3UploadErrorMessage,
  toUploadableFile,
  useS3Upload,
} from './helpers/s3-upload';
import Image from './Image.vue';

type GalleryImage = {
  id: string;
  url: string;
};

type UploadResult = {
  public_id: string;
  secure_url: string;
};

type Props = {
  modelValue: GalleryImage[];
  maxLength: number;
  cloudinaryPreset: string;
  imaginaryCategory?: ImaginaryCategory;
  enforceLandscape?: boolean;
  minWidth?: number;
  minHeight?: number;
  uploadTestMode?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  enforceLandscape: false,
  minWidth: 0,
  minHeight: 0,
  uploadTestMode: false,
});

const useS3 = computed(() => !!props.imaginaryCategory);

const emit = defineEmits<{
  (event: 'update:modelValue', value: GalleryImage[]): void;
  (event: 'image-dropped', file: File): void;
  (event: 'image-rejected', reason: ImageRejection): void;
  (event: 'upload-started'): void;
  (event: 'upload-error', error: Error): void;
}>();

const { t } = useI18n();

const { isUploading: isCloudinaryUploading, uploadImage } =
  useCloudinaryUpload();

const { isUploading: isS3Uploading, uploadImage: uploadImageToS3 } =
  useS3Upload();

const isDraggingOver = ref(false);
const isBatchUploading = ref(false);
const isTestUploading = ref(false);
const showFileSizeError = ref(false);
const uploadFailure = ref<Error | null>(null);
const currentUploadIndex = ref(0);
const totalUploadCount = ref(0);

const maxFileSize = 10000000;

const images = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  },
});

const hasImages = computed(() => images.value.length > 0);

const canAddImage = computed(() => images.value.length < props.maxLength);

const isUploading = computed(
  () =>
    isBatchUploading.value ||
    isCloudinaryUploading.value ||
    isS3Uploading.value ||
    isTestUploading.value,
);

const uploadFailureText = computed(() =>
  useS3.value
    ? s3UploadErrorMessage(uploadFailure.value, t)
    : t('global.gallery.upload_failed'),
);

const uploadStatusText = computed(() => {
  if (isUploading.value && totalUploadCount.value > 1) {
    return t('global.gallery.uploading_photos', {
      current: currentUploadIndex.value,
      total: totalUploadCount.value,
    });
  }

  if (isUploading.value) {
    return t('global.gallery.uploading_photo');
  }

  return hasImages.value
    ? t('global.gallery.add_photos')
    : t('global.gallery.empty_upload');
});

const removeImage = (id: string) => {
  images.value = images.value.filter((image) => image.id !== id);
};

const getUploadableFiles = (files?: FileList | null) => {
  if (!files) {
    return [];
  }

  const availableSlots = Math.max(0, props.maxLength - images.value.length);

  return Array.from(files)
    .filter((file) => {
      // The S3 path converts HEIC itself, including picks the browser gives no type
      if (
        !file.type.startsWith('image/') &&
        !(useS3.value && isHeicFile(file))
      ) {
        return false;
      }

      if (file.size > maxFileSize) {
        showFileSizeError.value = true;

        return false;
      }

      return true;
    })
    .slice(0, availableSlots);
};

const waitForTestUpload = () =>
  new Promise((resolve) => {
    window.setTimeout(resolve, 2000);
  });

const uploadTestImage = async (file: File): Promise<UploadResult> => {
  isTestUploading.value = true;

  try {
    await waitForTestUpload();

    const testUrl = URL.createObjectURL(file);

    return {
      public_id: testUrl,
      secure_url: testUrl,
    };
  } finally {
    isTestUploading.value = false;
  }
};

const uploadGalleryImage = (file: File) => {
  if (props.uploadTestMode) {
    return uploadTestImage(file);
  }

  return props.imaginaryCategory
    ? uploadImageToS3(file, props.imaginaryCategory)
    : uploadImage(file, props.cloudinaryPreset);
};

const uploadImageFiles = async (files?: FileList | null) => {
  if (isUploading.value) {
    return;
  }

  showFileSizeError.value = false;
  uploadFailure.value = null;

  const uploadableFiles = getUploadableFiles(files);

  if (!uploadableFiles.length) {
    return;
  }

  isBatchUploading.value = true;
  totalUploadCount.value = uploadableFiles.length;
  emit('upload-started');

  try {
    for (const [index, file] of uploadableFiles.entries()) {
      currentUploadIndex.value = index + 1;
      emit('image-dropped', file);

      try {
        const uploadableFile = useS3.value
          ? await toUploadableFile(file)
          : file;

        if (hasImageRequirements(props)) {
          const rejection = await findImageRejection(uploadableFile, props);

          if (rejection) {
            emit('image-rejected', rejection);
            continue;
          }
        }

        const result = await uploadGalleryImage(uploadableFile);

        images.value = [
          ...images.value,
          {
            id: result.public_id,
            url: result.secure_url,
          },
        ];
      } catch (error) {
        uploadFailure.value =
          error instanceof Error
            ? error
            : new Error(t('global.gallery.upload_failed'));

        emit('upload-error', uploadFailure.value);
      }
    }
  } finally {
    isBatchUploading.value = false;
    currentUploadIndex.value = 0;
    totalUploadCount.value = 0;
  }
};

const handleDrop = (event: DragEvent) => {
  isDraggingOver.value = false;
  void uploadImageFiles(event.dataTransfer?.files);
};

const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;

  void uploadImageFiles(input.files);
  input.value = '';
};
</script>

<style
  module
  lang="scss"
>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: $spacing;
}

.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing * 0.5;
  aspect-ratio: 1 / 1;
  border: 2px dashed var(--border-strong);
  border-radius: 12px;
  cursor: pointer;

  @include hover {
    background-color: var(--highlight);
  }
}

.dropzoneEmpty {
  grid-column: 1 / -1;
  width: 100%;
  height: 200px;
  aspect-ratio: auto;
}

.dropzoneDragging {
  border-style: solid;
  background-color: var(--highlight);
}

.dropzoneUploading {
  pointer-events: none;
  opacity: 0.65;
}

.input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background-color: var(--highlight);
  transition: border-radius 0.15s ease-out;

  .dropzoneUploading & {
    border-radius: 50%;
  }
}
</style>
